import React, { useEffect, useState, useContext } from "react";
import classnames from "classnames";
import * as notify from "../../shared/notify";
import { ViewOnlyPermissionsContext } from "contexts/view-only-permissions/view-only-permissions";
import { getTaskTypes } from "../../components/task-list/lib/taskData";
import BulkSet from "./components/bulk-set";
import TaskNav from "../../components/task-nav/task-nav";
import TaskList from "./components/task-list";
import { SECTION_MAP } from "../../components/task-list/lib/constants";
import { useLocations } from "components/search-advanced-filters/hooks/use-locations";
import { useGetAssignmentOptions } from "shared/hooks/api/use-get-assignment-options";
import { useAvailableUserAssignments } from "../../shared/hooks/api/use-available-user-assignments";
import { createUniqueUsers } from "./lib/helpers";
import { PRIORITY_LEVELS } from "./lib/constants";
import "./assignment.scss";
import { Profiling } from "./components/profiling";
import { DEFAULT_FILTER, DEFAULT_SORT } from "../../components/scheduling-list/lib/constants";

const PERSISTED_FILTER = [{ id: "state", value: "active" }];

const AssignmentIndex = () => {
    const { viewOnlyAdmin } = useContext(ViewOnlyPermissionsContext);
    const [pulse, setPulse] = useState(false);
    const [selected, setSelected] = useState([]);
    const [taskType, setTaskType] = useState("");
    const [taskStatuses, setTaskStatuses] = useState({});
    const [state, setState] = useState({
        assignments: [],
        locations: [],
        priorityLevels: [],
        isLoaded: false,
    });
    const [usersWithAssignments, setUsersWithAssignments] = useState([]);
    const [teamFilter, setTeamFilter] = useState("");
    const [appendSettings, setAppendSettings] = useState({
        filtered: [...PERSISTED_FILTER],
    });
    const { data: locations, isSuccess: locationsLoaded } = useLocations();
    const { data: assignments, isSuccess: assignmentsLoaded } = useGetAssignmentOptions();
    const { data: availableUserAssignments, isSuccess } = useAvailableUserAssignments();
    const isProfilingSelected = taskType === "scheduling";

    useEffect(() => {
        if (locationsLoaded && assignmentsLoaded) {
            setState({
                ...state,
                assignments,
                locations,
                priorityLevels: PRIORITY_LEVELS,
                isLoaded: true,
            });
        }
    }, [locations, locationsLoaded, assignments, assignmentsLoaded]);

    useEffect(() => {
        if (isSuccess && availableUserAssignments.length) {
            setUsersWithAssignments(createUniqueUsers(availableUserAssignments, teamFilter));
        }
    }, [isSuccess, teamFilter, availableUserAssignments.length]);

    const updateTaskType = async (value) => {
        const isProfiling = value === "scheduling";
        setSelected([]);
        setTaskType(value);

        setAppendSettings({
            page: 1,
            sorted: isProfiling ? DEFAULT_SORT : [{ id: "due", desc: false }],
            filtered: isProfiling ? DEFAULT_FILTER : [{ id: "taskType", value: value }, ...PERSISTED_FILTER],
        });

        if (!taskStatuses[value]) {
            const data = await getTaskTypes(value, isProfiling ? "default" : "standard");

            if (!data) {
                notify.error("Error getting task statuses, please try again.");

                return;
            }

            const statuses = data.applicableStates.reduce((arr, t) => {
                if (t.state !== "completed") {
                    arr.push({ text: t.description, value: t.state, stateId: t.stateId });
                }

                return arr;
            }, []);
            const newTaskStatus = {
                ...taskStatuses,
                [value]: statuses,
            };
            setTaskStatuses(newTaskStatus);
        }
    };

    const setSelectedProducts = (values) => {
        setSelected(values);
    };

    const triggerTableReset = () => {
        setSelected([]);
        setPulse(!pulse);
    };

    if (!state.isLoaded) {
        return null;
    }

    const taskTypeButtons = Object.keys(SECTION_MAP).reduce((arr, key) => {
        if (key !== "measuring" && key !== "duplicatestaging") {
            arr.push(
                <button
                    key={`button_${key}`}
                    className={classnames("button", { "-primary": taskType === key })}
                    onClick={() => updateTaskType(key)}
                >
                    {SECTION_MAP[key].label || ""}
                </button>
            );
        }

        return arr;
    }, []);

    return (
        <div>
            <TaskNav>
                <div className="container">
                    <h1>Task Assignment</h1>
                    <BulkSet
                        taskType={taskType}
                        priorityLevels={state.priorityLevels}
                        availableUserAssignments={availableUserAssignments}
                        assignments={state.assignments}
                        selected={selected}
                        updateTaskType={updateTaskType}
                        triggerTableReset={triggerTableReset}
                        disableNotes={viewOnlyAdmin.createAssignmentNote}
                        disableTeamAssignment={viewOnlyAdmin.assignToTeam}
                        disableUserAssignment={viewOnlyAdmin.assignToUser}
                    />
                </div>
            </TaskNav>

            <div className="task-type-buttons button-group flex-wrap">{taskTypeButtons}</div>

            {isProfilingSelected && taskStatuses[taskType] && (
                <Profiling
                    taskType={taskType}
                    usersWithAssignments={usersWithAssignments}
                    appendSettings={appendSettings}
                    assignments={state.assignments}
                    locations={state.locations}
                    priorityLevels={state.priorityLevels}
                    setSelectedProducts={setSelectedProducts}
                    selected={selected}
                    updatePulse={pulse}
                    statuses={taskStatuses[taskType]}
                    setTeamFilter={setTeamFilter}
                    disableNotes={viewOnlyAdmin.createAssignmentNote}
                />
            )}
            {taskType && taskStatuses[taskType] && !isProfilingSelected && (
                <TaskList
                    taskType={taskType}
                    usersWithAssignments={usersWithAssignments}
                    appendSettings={appendSettings}
                    assignments={state.assignments}
                    locations={state.locations}
                    priorityLevels={state.priorityLevels}
                    setSelectedProducts={setSelectedProducts}
                    selected={selected}
                    updatePulse={pulse}
                    statuses={taskStatuses[taskType]}
                    setTeamFilter={setTeamFilter}
                    disableNotes={viewOnlyAdmin.createAssignmentNote}
                />
            )}
            {!taskType && <h1 className="txt-center">Please select a task type</h1>}
        </div>
    );
};

export default AssignmentIndex;
